import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useTranslation } from 'react-i18next';

import {
  changeAppLanguage,
  clearStoredLanguage,
  getBrowserLanguageFocus,
  getCurrentLanguage,
  getLanguageDirection,
  hasStoredLanguage,
  isAppLanguage,
  LANGUAGE_STORAGE_KEY,
  type AppDirection,
  type AppLanguage,
} from '../../i18n';
import { FirstRunLanguageDialog } from './FirstRunLanguageDialog';

export interface LanguageContextValue {
  readonly language: AppLanguage;
  readonly direction: AppDirection;
  readonly hasSelectedLanguage: boolean;
  readonly isChangingLanguage: boolean;
  readonly selectLanguage: (language: AppLanguage) => Promise<void>;
  readonly toggleLanguage: () => Promise<void>;
  readonly resetLanguageChoice: () => void;
}

export const LanguageContext = createContext<LanguageContextValue | null>(null);

export interface LanguageProviderProps {
  readonly children: ReactNode;
}

export function LanguageProvider({ children }: LanguageProviderProps) {
  const { i18n: activeI18n } = useTranslation();
  const [hasSelectedLanguage, setHasSelectedLanguage] = useState(hasStoredLanguage);
  const [isChangingLanguage, setIsChangingLanguage] = useState(false);
  const [suggestedLanguage] = useState(getBrowserLanguageFocus);

  const resolvedLanguage = activeI18n.resolvedLanguage ?? activeI18n.language;
  const language = isAppLanguage(resolvedLanguage) ? resolvedLanguage : getCurrentLanguage();

  const selectLanguage = useCallback(async (nextLanguage: AppLanguage) => {
    setIsChangingLanguage(true);

    try {
      await changeAppLanguage(nextLanguage);
      setHasSelectedLanguage(true);
    } finally {
      setIsChangingLanguage(false);
    }
  }, []);

  const toggleLanguage = useCallback(async () => {
    await selectLanguage(language === 'ar' ? 'en' : 'ar');
  }, [language, selectLanguage]);

  const resetLanguageChoice = useCallback(() => {
    clearStoredLanguage();
    setHasSelectedLanguage(false);
  }, []);

  useEffect(() => {
    const handleStorage = (event: StorageEvent) => {
      if (event.key !== LANGUAGE_STORAGE_KEY && event.key !== null) {
        return;
      }

      if (isAppLanguage(event.newValue)) {
        void changeAppLanguage(event.newValue, { persist: false });
        setHasSelectedLanguage(true);
        return;
      }

      setHasSelectedLanguage(false);
    };

    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  const value = useMemo<LanguageContextValue>(
    () => ({
      language,
      direction: getLanguageDirection(language),
      hasSelectedLanguage,
      isChangingLanguage,
      selectLanguage,
      toggleLanguage,
      resetLanguageChoice,
    }),
    [
      language,
      hasSelectedLanguage,
      isChangingLanguage,
      selectLanguage,
      toggleLanguage,
      resetLanguageChoice,
    ],
  );

  return (
    <LanguageContext.Provider value={value}>
      {children}
      <FirstRunLanguageDialog
        open={!hasSelectedLanguage}
        suggestedLanguage={suggestedLanguage}
        busy={isChangingLanguage}
        onSelect={selectLanguage}
      />
    </LanguageContext.Provider>
  );
}
